<template>
  <div>
    <m-row class="mt-4">
      <m-col lg="4" v-for="field in exportableFields" :key="field.key">
        <memlist-checkbox
          :id="`field-${field.key}`"
          v-model="member_fields[field.key]"
          :text="field.label"
        />
      </m-col>
    </m-row>

    <!-- Info rows per selected field -->
    <div class="mt-4" v-for="field in exportableFields" :key="'info-' + field.key">
      <div class="ml-alert ml-alert-info" v-if="member_fields[field.key] && field.group && groupExplanations[field.group]">
        <strong>📘 {{ field.label }}:</strong>
        <span>
          {{ groupExplanations[field.group] }}
        </span>

      </div>
    </div>

    <memlist-button class="mt-4" @click="generate_excel_clicked" :disabled="disable_export">
      {{ $t('EXPORT.GENERATE_EXCEL') }}
    </memlist-button>
  </div>
</template>

<script>
import axios from 'axios';
import { get_base_url, downloadWithAxios } from '@/core/services/fileDownload';

export default {
  name: 'GenerateImportExcelComponent',
  computed: {
    disable_export() {
      const selectedFields = Object.entries(this.member_fields)
        .filter(([_, value]) => value)
        .map(([key]) => key);

      return selectedFields.length === 0;
    }
  },
  data() {
    return {
      member_fields: {},
      exportableFields: [
        { key: 'public_id', label: this.$t('MEMBER.PUBLIC_ID'), examples: ['10001', '10002', '10003', '10004'] },
        { key: 'in_personnr', label: this.$t('MEMBER.IN_PERSONNR'), examples: ['19960723-5463', '19780711-8679', '20060507-2594', '19810214-6573'] },

        { key: 'firstname', label: this.$t('MEMBER.FIRSTNAME'), examples: ['Jens', 'Maria', 'Anna', 'Oskar'] },
        { key: 'lastname', label: this.$t('MEMBER.LASTNAME'), examples: ['Bengtsson', 'Eriksson', 'Lindström', 'Lindström'] },

        { key: 'email', label: this.$t('MEMBER.EMAIL'), examples: ['jens@example.com', 'anna.lind@example.se', 'anna.b1233@lindstrom.se', 'oskar.34234234234@lindstrom.se'] },
        { key: 'email2', label: this.$t('MEMBER.EMAIL2'), examples: ['backup@example.com', 'extra@mail.se', 'info@altmail.se', 'second@post.nu'] },
        { key: 'phone', label: this.$t('MEMBER.PHONE'), examples: ['0701234567', '+46701122334', '0729876543', '0734567890'] },
        { key: 'landline', label: this.$t('MEMBER.LANDLINE'), examples: ['031123456', '085551234', '040987654', '018556677'] },

        { key: 'address', label: this.$t('MEMBER.ADDRESS'), examples: ['Storgatan 5', 'Box 123', 'Vägen 12A', 'Vägen 12A'] },
        { key: 'co', label: this.$t('MEMBER.CO'), examples: ['Andersson', 'Tech AB', '', ''] },
        { key: 'post', label: this.$t('MEMBER.POST'), examples: ['Göteborg', 'Uppsala', 'Stockholm', 'Stockholm'] },
        { key: 'zipcode', label: this.$t('MEMBER.ZIPCODE'), examples: ['41104', '75320', '11455', '11455'] },
        { key: 'country', label: this.$t('MEMBER.COUNTRY'), examples: ['SE', 'NO', 'SE', 'SE'] },
        { key: 'region_code', label: this.$t('MEMBER.REGION_CODE'), examples: ['10', '14', '01', '01'] },
        { key: 'commune_code', label: this.$t('MEMBER.COMMUNE_CODE'), examples: ['0180', '0480', '1280', '1280'] },

        { key: 'invoice_address', label: this.$t('MEMBER.INVOICE_ADDRESS'), examples: ['Fakturagatan 1', 'Box 45', 'Industrivägen 3', 'Industrivägen 3'] },
        { key: 'invoice_zipcode', label: this.$t('MEMBER.INVOICE_ZIPCODE'), examples: ['12345', '11357', '22345', '22345'] },
        { key: 'invoice_email', label: this.$t('MEMBER.INVOICE_EMAIL'), examples: ['faktura@example.com', 'invoice@firma.se', 'admin@bolag.nu', 'redovisning@mail.se'] },
        { key: 'invoice_phone', label: this.$t('MEMBER.INVOICE_PHONE'), examples: ['0812345678', '0822334455', '0833445566', '0844556677'] },
        { key: 'invoice_post', label: this.$t('MEMBER.INVOICE_POST'), examples: ['Stockholm', 'Malmö', 'Stockholm', 'Stockholm'] },
        { key: 'invoice_country', label: this.$t('MEMBER.INVOICE_COUNTRY'), examples: ['SE', 'FI', 'SE', 'SE'] },

        { key: 'dob', label: this.$t('MEMBER.DOB'), group: 'date', examples: ['2001-04-18', '1999-12-01', '1975-03-22', '1984-08-09'] },
        { key: 'public_sex', label: this.$t('MEMBER.PUBLIC_SEX'), examples: ['M', 'F', 'O', 'U'] },

        { key: 'rdt', label: this.$t('MEMBER.RDT'), group: 'date', examples: ['2022-03-01', '2021-09-10', '2020-01-15', '2023-11-05'] },
        { key: 'accepted_at', label: this.$t('MEMBER.ACCEPTED_AT'), group: 'date', examples: ['2024-01-12', '2023-07-19', '2022-06-03', '2024-03-30'] },
        { key: 'deactivate_at', label: this.$t('MEMBER.DEACTIVATE_AT'), group: 'date', examples: ['2025-01-01', '2024-12-31', '2026-06-30', '2024-09-15'] },
        { key: 'vfdt', label: this.$t('MEMBER_COMPANY.VFDT'), group: 'date', examples: ['2024-09-01', '2023-09-01', '2025-01-01', '2024-01-01'] },
        { key: 'vtdt', label: this.$t('MEMBER_COMPANY.VTDT'), group: 'date', examples: ['2025-08-31', '2024-08-31', '2025-06-30', '2026-12-31'] },

        { key: 'status', label: this.$t('MEMBER.STATUS'), examples: ['member', 'rejected', 'pending', 'waiting'] },
        { key: 'org_number', label: this.$t('MEMBER.ORG_NUMBER'), examples: ['556677-8899', '730822-1024', '', ''] },
        { key: 'cname', label: this.$t('MEMBER.COMPANY_NAME'), examples: ['AB Bygg & Co', 'Lindqvist Konsult', '', ''] },
        { key: 'reg_at', label: this.$t('MEMBER_COMPANY.REG_AT'), group: 'date', examples: ['2025-08-31', '2024-08-31', '2025-06-30', '2026-12-31'] },
        
        { key: 'title', label: this.$t('MEMBER.TITLE'), examples: ['Ordförande', 'Sekreterare', 'Ledamot', 'Kassör'] },


        { key: 'map_company', label: this.$t('MEMBER.MAP_COMPANY'), group: 'map_company', examples: ['1','3','4','99'] },
        { key: 'map_family', label: this.$t('MEMBER.MAP_FAMILY'), group: 'map_family' },
        { key: 'map_membership', label: this.$t('MEMBER.MAP_MEMBERSHIPS'), group: 'map_membership', examples: ['1,2,3','99','132','123,124'] },
        { key: 'map_parent', label: this.$t('MEMBER.MAP_PARENT'), group: 'mapping' },
      ],

      groupExplanations: {
        date: '📅 Varje datumvärde måste formateras som ÅÅÅÅ-MM-DD, exempel: 2005-01-12',
        map_company: '🔗 Ange "föreningsnummer" för att koppla till respektive förening, exempel: 1',
        map_parent: '🔗 För vårdnadshavare måste man ange "Är vårdnadshavare" 1 (Vårdnadshavaren) respektive 0 (Är medlemmen). Här måste du även ha kolumnen FamiljeID ',
        map_family: '🔗 Huvudmedlem måste anges: 1 eller 0. 1 är huvudmedlem (alltså fakturamottagaren), 0 är familjemedlemmen. Även ett FamiljeID måste ställas in. Ifall Bengt, Lisa och Anders är samma familj, måste alla ha samma FamiljeID, till exempel: 101',
        map_membership: '🔗 Flera medlemskapstillhörigheter kan importeras genom att i kolumnen "Medlemskap" sätta in Föreningsnummer med separation av kommatecken, exempel: 1,4,8',
      }
    }
  },

  mounted() {
    this.exportableFields.forEach(f => {
      this.$set(this.member_fields, f.key, false);
    });
  },

  methods: {
    isGroupSelected(group) {
      return this.exportableFields.some(
        f => f.group === group && this.member_fields[f.key]
      );
    },

    async generate_excel_clicked() {
      const selectedFields = Object.entries(this.member_fields)
        .filter(([_, value]) => value)
        .map(([key]) => key);

      if (selectedFields.length === 0) {
        return;
      }

      const fields = [];

      for (const field of selectedFields) {
        const item = this.exportableFields.find(i => i.key === field);
        fields.push(item);
      }

      const res = await axios.post('/import/generate_template', {
        fields: fields
      });

      if (res.status === 201) {
        downloadWithAxios(get_base_url() + `/dlfile/${res.data.linkstr}`, res.data.name);
      }
      else {
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('EXPORT.UNABLE_EXPORT'));
      }
      
    }
  }
}
</script>

<style scoped>
.ml-alert {
  background-color: #eef6fb;
  border: 1px solid #bce0f1;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  color: black;
}
</style>
